import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useLoginUserMutation,
  useSendOtpMutation,
  useVerifyOtpMutation,
  authApi,
} from '../app/services/authApi';
import { useDispatch, useSelector } from 'react-redux';
import {
  userLoginRequest,
  userLoginFailure,
  userUpdateViewType,
} from '../app/reducers/userInfo/slice';
import { addBrand } from '../app/reducers/myBrand/slice';
import { setError } from '../app/reducers/auth/slice';
import toastService from '../services/toastService';
import { Eye, EyeOff, Mail, Phone, PhoneIcon } from 'lucide-react';
import logoSrc2 from '../assets/logo.png';
import OTPVerification from '../Components/Ui/OTPVerification';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneOTP, setPhoneOTP] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneOTPSent, setPhoneOTPSent] = useState(false);
  const [sendingPhoneOTP, setSendingPhoneOTP] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const { user, isLoggedIn, viewType } = useSelector((state) => state.userInfo);
  const [login] = useLoginUserMutation();
  const [sendOtp] = useSendOtpMutation();
  const [verifyOtp] = useVerifyOtpMutation();

  useEffect(() => {
    if (isLoggedIn && user?.role) {
      const userRoles = Array.isArray(user.role) ? user.role : [user.role];

      if (userRoles.includes('brand') && userRoles.includes('seller')) {
        // If viewType is not set, navigate to select-view
        if (!viewType) {
          navigate('/select-view');
        } else if (viewType === 'brand') {
          navigate('/brand/products');
        } else if (viewType === 'seller') {
          navigate('/seller/brand-catalog');
        }
      } else if (userRoles.includes('brand')) {
        // For single-role brand users, ensure viewType is set and navigate
        if (viewType === 'brand') {
          navigate('/brand/products');
        } else {
          // If viewType is not yet 'brand', dispatch it
          dispatch(userUpdateViewType('brand'));
        }
      } else if (userRoles.includes('seller')) {
        // For single-role seller users, ensure viewType is set and navigate
        if (viewType === 'seller') {
          navigate('/seller/brand-catalog');
        } else {
          // If viewType is not yet 'seller', dispatch it
          dispatch(userUpdateViewType('seller'));
        }
      } else if (userRoles.includes('admin')) {
        navigate('/admin/users');
      } else {
        toastService.info('You do not have a valid role to proceed.');
      }
    }
  }, [isLoggedIn, user, viewType, navigate, dispatch]);

  useEffect(() => {
    let timer;
    if (isResending && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setIsResending(false);
    }
    return () => clearInterval(timer);
  }, [isResending, resendTimer]);

  const handleBack = () => {
    setPhoneOTPSent(false);
    setPhoneOTP('');
    setCurrentSessionId('');
  };

  const handleSendPhoneOTP = async () => {
    if (!phoneNumber) {
      toastService.error('Please enter your phone number');
      return;
    }
    if (phoneNumber.length !== 10 || !/^[0-9]+$/.test(phoneNumber)) {
      toastService.error('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      setSendingPhoneOTP(true);
      setIsResending(true);
      setResendTimer(45);
      const response = await sendOtp({ phoneNumber, otpType: 'login' }).unwrap();
      if (response?.success) {
        setCurrentSessionId(response?.sessionId);
        setPhoneOTPSent(true);
        toastService.success('OTP sent Successfully');
      } else {
        toastService.error(response?.message || 'Failed to send OTP');
      }
    } catch (error) {
      const errorMessage = error?.data?.message || 'Failed to send OTP. Please try again.';
      dispatch(setError(errorMessage));
      toastService.error(errorMessage);
    } finally {
      setSendingPhoneOTP(false);
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    if (!currentSessionId || !phoneOTP) {
      dispatch(setError('Please enter the OTP and ensure you have a session.'));
      return;
    }
    try {
      // dispatch(userLoginRequest());
      const response = await verifyOtp({
        sessionId: currentSessionId,
        phoneOtp: phoneOTP,
      }).unwrap();

      if (response?.isNewUser) {
        toastService.info(response?.message || 'New user detected. Please complete registration.');
        // Navigate to signup page, possibly passing verifiedContact data
        navigate('/signup', { state: { verifiedContact: response.verifiedContact } });
      } else {
        handleLoginSuccess(response);
      }
    } catch (error) {
      handleLoginError(error);
    }
  };

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      dispatch(setError('Please enter your email and password'));
      return;
    }
    try {
      dispatch(userLoginRequest());
      const response = await login({ email, password }).unwrap();
      handleLoginSuccess(response);
    } catch (error) {
      handleLoginError(error);
    }
  };

  const handleLoginSuccess = async (response) => {
    if (response?.success) {
      const user = response?.user;
      if (user) {
        const userRoles = Array.isArray(user.role) ? user.role : [user.role];
        toastService.success('Login Successfully', { toastId: 'login-success' });
        if (userRoles.includes('brand') && userRoles.includes('seller')) {
          const userId = user.userId || user._id;
          if (userId) {
            try {
              const brandPromise = dispatch(
                authApi.endpoints.myBrandInfo.initiate(userId, { forceRefetch: true }),
              );
              const brandData = await brandPromise.unwrap();
              if (brandData?.data) {
                dispatch(addBrand(brandData.data));
              }
              brandPromise.unsubscribe();
            } catch (error) {
              toastService.error('Could not fetch brand information.');
            }
          } else {
            toastService.error('User ID not available to fetch brand information.');
          }
          navigate('/select-view');
        } else if (userRoles.includes('brand')) {
          const userId = user.userId || user._id;
          if (userId) {
            try {
              const brandPromise = dispatch(
                authApi.endpoints.myBrandInfo.initiate(userId, { forceRefetch: true }),
              );
              const brandData = await brandPromise.unwrap();
              if (brandData?.data) {
                dispatch(addBrand(brandData.data));
              }
              brandPromise.unsubscribe();
              dispatch(userUpdateViewType('brand'));
              navigate('/brand/products');
            } catch (error) {
              toastService.error('Could not fetch brand information.');
            }
          } else {
            toastService.error('User ID not available to fetch brand information.');
          }
        } else if (userRoles.includes('seller')) {
          dispatch(userUpdateViewType('seller'));
          navigate('/seller/brand-catalog');
        } else if (userRoles.includes('admin')) {
          navigate('/admin/users');
        } else {
          toastService.info('You do not have a valid role to proceed.');
        }
      } else {
        dispatch(userLoginFailure('Login response was successful, but no user data was received.'));
      }
    } else {
      dispatch(userLoginFailure(response?.message || 'Login Failed, Please try again'));
      toastService.error(response?.message || 'Login Failed, Please try again');
    }
  };

  const handleLoginError = (error) => {
    toastService.error(error?.data?.message || 'Failed to Login. Please try again.');
  };

  const handleSignupClick = () => {
    navigate('/signup');
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <img src="/Winndo.png" alt="" className="absolute inset-0 -z-10 h-full w-full" />
      <div className="absolute inset-0 z-0 bg-[rgba(28,28,28,0.75)] transition-opacity duration-200" />
      <div className="max-w-md w-full tertiary-background backdrop-blur-xl p-7 md:mx-10 mx-5 rounded-2xl shadow-2xl border border-white/40 space-y-4 transition-all duration-300 hover:shadow-orange-500/40">
        <div className="flex items-center justify-center h-[10px] ">
          <div className=" flex items-center">
            <img src={logoSrc2} alt="logoAlt1" className="w-[145px] h-[130px]" />
          </div>
        </div>
        <div className="text-lg font-bold text-primary flex justify-center">Partner Login</div>

        {!showEmailLogin && (
          <div className="space-y-4">
            {!phoneOTPSent ? (
              <>
                <div className="space-y-1">
                  <label className="flex items-center gap-2 text-base font-medium text-secondary">
                    <PhoneIcon size={16} className="text-primary" />
                    Enter a mobile number to continue
                  </label>
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full px-4 py-2 border  border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 border-color shadow-sm"
                  />
                </div>
                <button
                  onClick={handleSendPhoneOTP}
                  disabled={sendingPhoneOTP}
                  className="w-full px-4 py-2 rounded-lg text-sm font-medium white-text primary-background hover:opacity-90 transition-all duration-200 shadow-md"
                >
                  {sendingPhoneOTP ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={handleBack}
                    className="text-primary text-3xl font-bold leading-none"
                  >
                    ←
                  </button>
                  <div className="w-6" />
                </div>
                <OTPVerification
                  phoneNumber={phoneNumber}
                  otp={phoneOTP}
                  setOtp={setPhoneOTP}
                  onResend={handleSendPhoneOTP}
                  onVerify={handlePhoneLogin}
                  isResending={isResending}
                  resendTimer={resendTimer}
                />
              </div>
            )}
          </div>
        )}

        {showEmailLogin && (
          <div className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300  rounded-lg text-sm focus:outline-none focus:ring-2 border-color shadow-sm"
            />

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 border-color shadow-sm"
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-primary"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </span>
            </div>

            <div className="text-right mt-1">
              <span
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-primary cursor-pointer underline"
              >
                Forgot Password?
              </span>
            </div>

            <button
              onClick={handleEmailPasswordLogin}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium white-text primary-background transition-all duration-200 shadow-md"
            >
              Log In
            </button>
          </div>
        )}
        {!phoneOTPSent && (
          <div className="flex flex-col items-center space-y-2">
            {!showEmailLogin && (
              <p
                onClick={() => {
                  setShowEmailLogin(true);
                  setPhoneOTPSent(false);
                  setPhoneOTP('');
                  setCurrentSessionId('');
                }}
                className="flex items-center gap-2 text-base border border-gray-400 border-lg py-1.5 px-4 rounded-lg font-medium text-primary cursor-pointer hover:underline"
              >
                <Mail size={24} /> Login via Email
              </p>
            )}

            {showEmailLogin && (
              <p
                onClick={() => {
                  setShowEmailLogin(false);
                  setPhoneOTPSent(false);
                  setPhoneOTP('');
                  setCurrentSessionId('');
                }}
                className="flex items-center gap-2 text-base border border-gray-400 border-lg py-1.5 px-4 rounded-lg font-medium text-primary cursor-pointer hover:underline"
              >
                <Phone size={24} /> Login via Phone
              </p>
            )}
            <p className="text-sm text-secondary">Or</p>

            <p className="text-base">
              Don’t have an account?{' '}
              <span onClick={handleSignupClick} className="text-primary underline cursor-pointer">
                Sign Up
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
